
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { getFirestore, collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { Routes, Route, Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import Talk from './pages/Talk';
import Billboard from './pages/Billboard';
import Lyrics from './pages/Lyrics';
import Fly from './pages/Fly';
import Configure from './pages/Configure';
import './App.scss';

interface Example {
  id: string;
  level: number;
  songId: string;
  imageFull: string;
  imageUrl: string;
  index: number;
  twisted: string[];
  text: string;
  imagePermanentLink: string;
  translation: Record<string, string>;
}

interface Song {
  id: string;
  name: string;
  artist: string;
  position: number;
  yt: string[];
  ytIndex: number;
  translation: Record<string, string>;
}

interface Level {
  level: number;
  name: string;
  image: string;
}

interface Lesson {
  id: string;
  level: number;
  order: number;
  content: Record<string, unknown>[];
}
const BackIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12"></line>
    <polyline points="12 19 5 12 12 5"></polyline>
  </svg>
);

const App: React.FC = () => {
  const db = getFirestore();
  const navigate = useNavigate();
  const [examples, setExamples] = useState<Example[]>([]);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [top10Songs, setTop10Songs] = useState<Song[]>([]);
  const [isNavVisible, setIsNavVisible] = useState(false);
  const [showSongList, setShowSongList] = useState(false);
  const [showLevelList, setShowLevelList] = useState(false);
  const [currentLevel, setCurrentLevel] = useState<number>(() => {
    try {
      const v = localStorage.getItem('musi_currentLevel');
      return v ? Number(v) : 1;
    } catch (e) {
      return 1;
    }
  });
  const [currentSong, setCurrentSong] = useState(0);
  const [currentLanguage, setCurrentLanguage] = useState<string>(() => {
    try {
      return localStorage.getItem('musi_currentLanguage') || 'es';
    } catch (e) {
      return 'es';
    }
  });
  const [translate, setTranslate] = useState<boolean>(() => {
    try {
      return localStorage.getItem('musi_translate') === 'true';
    } catch (e) {
      return false;
    }
  });
  const [levels, setLevels] = useState<Level[]>([]);
  const songListRef = useRef<HTMLDivElement | null>(null);
  const levelListRef = useRef<HTMLDivElement | null>(null);
  const navRef = useRef<HTMLUListElement | null>(null);

  const getMenuIcon = (label: string) => {
    // Return a small representative SVG icon as a data URL for each menu label
    const icons: Record<string, string> = {
      Talk: `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='48' height='48'><path fill='%23ffffff' d='M20 2H4c-1.1 0-2 .9-2 2v14l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z'/></svg>`,
      Billboard: `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='48' height='48'><path fill='%23ffffff' d='M3 13h4v8H3zM10 7h4v14h-4zM17 3h4v18h-4z'/></svg>`,
      Lyrics: `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='48' height='48'><path fill='%23ffffff' d='M12 3v10.55A4 4 0 1 0 14 17V7h4V3h-6z'/></svg>`,
      Learn: `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='48' height='48'><path fill='%23ffffff' d='M6 2h12v16l-6-2-6 2V2z'/></svg>`,
      Practice: `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='48' height='48'><path fill='%23ffffff' d='M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z'/></svg>`,
      Play: `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='48' height='48'><path fill='%23ffffff' d='M8 5v14l11-7z'/></svg>`,
      Select: `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='48' height='48'><path fill='%23ffffff' d='M19 3h-4.18C14.4 1.84 13.3 1 12 1s-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM10 17l-3-3 1.41-1.41L10 14.17 15.59 8.59 17 10l-7 7z'/></svg>`,
      Configure: `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='48' height='48'><path fill='%23ffffff' d='M19.14 12.94a7.07 7.07 0 0 0 0-1.88l2.03-1.58a.5.5 0 0 0 .12-.64l-1.92-3.32a.5.5 0 0 0-.6-.22l-2.39 1a7.14 7.14 0 0 0-1.6-.93L14.5 2.81a.5.5 0 0 0-.5-.31h-4a.5.5 0 0 0-.5.31L8.42 5.08c-.56.25-1.09.57-1.6.93l-2.39-1a.5.5 0 0 0-.6.22L2.91 8.54a.5.5 0 0 0 .12.64L5.06 10.76c-.06.62-.06 1.24 0 1.88L3 14.22a.5.5 0 0 0-.12.64l1.92 3.32c.14.24.44.34.6.22l2.39-1c.51.36 1.04.68 1.6.93L9.5 21.19c.09.18.26.31.5.31h4c.24 0 .41-.13.5-.31l1.08-2.27c.56-.25 1.09-.57 1.6-.93l2.39 1c.16.09.46 0 .6-.22l1.92-3.32a.5.5 0 0 0-.12-.64zM12 15.5A3.5 3.5 0 1 1 15.5 12 3.5 3.5 0 0 1 12 15.5z'/></svg>`
    };

    const svg = icons[label] || `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='48' height='48'><rect width='100%' height='100%' rx='6' fill='%23222'/><text x='50%' y='50%' font-size='10' fill='%23fff' text-anchor='middle' dominant-baseline='middle'>${label.slice(0, 2)}</text></svg>`;
    return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg);
  };

  useEffect(() => {
    if (!showSongList) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (songListRef.current && !songListRef.current.contains(e.target as Node)) {
        setShowSongList(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowSongList(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [showSongList]);

  useEffect(() => {
    if (!showLevelList) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (levelListRef.current && !levelListRef.current.contains(e.target as Node)) {
        setShowLevelList(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowLevelList(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [showLevelList]);

  useEffect(() => {
    if (!isNavVisible) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setIsNavVisible(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsNavVisible(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isNavVisible]);

  const toggleNav = () => {
    setIsNavVisible(!isNavVisible);
  };

  //levels object array taken from levels collection in firestore (on first load only), each object has a level number a name and an image url
  useEffect(() => {
    const fetchLevels = async () => {
      try {
        const levelsRef = collection(db, "levels");
        const snapshot = await getDocs(levelsRef);
        const levelsArray: Level[] = [];
        snapshot.forEach(doc => {
          const data = doc.data();
          if (data.level && data.name && data.image) {
            levelsArray.push({ level: data.level, name: data.name, image: data.image } as Level);
          }
        });
        levelsArray.sort((a, b) => a.level - b.level);
        setLevels(levelsArray);
        console.log('Levels loaded:', levelsArray);
      } catch (err) {
        console.error('Failed to load levels', err);
      }
    }
    fetchLevels();
  }, [db]);

  // Persist selected settings to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('musi_currentLevel', String(currentLevel));
    } catch (e) {
      // ignore
    }
  }, [currentLevel]);

  useEffect(() => {
    try {
      localStorage.setItem('musi_currentLanguage', currentLanguage);
    } catch (e) {
      // ignore
    }
  }, [currentLanguage]);

  useEffect(() => {
    try {
      localStorage.setItem('musi_translate', translate ? 'true' : 'false');
    } catch (e) {
      // ignore
    }
  }, [translate]);

  // Fetch songs and examples and lessons
  useEffect(() => {
    let mounted = true;

    async function getSongsArray(): Promise<{ songsArray: string[], top10Songs: Song[] }> {
      const songsRef = collection(db, "songs");
      const q = query(songsRef, where("position", ">=", 1), where("position", "<=", 10));
      const snapshot = await getDocs(q);

      const songsArray: string[] = new Array(10);
      const top10Songs: Song[] = [];

      snapshot.forEach(doc => {
        const data = doc.data();
        const pos = data.position;
        if (pos >= 1 && pos <= 10) {
          songsArray[pos - 1] = doc.id;
          top10Songs.push({ id: doc.id, ...data } as Song);
        }
      });

      return { songsArray, top10Songs };
    }

    async function getExamplesBySongs(level: number): Promise<Example[]> {
      const { songsArray, top10Songs } = await getSongsArray();
      if (mounted) setTop10Songs(top10Songs);

      const examplesRef = collection(db, "examples");
      const validSongIds = songsArray.filter(id => id !== undefined);
      const q = query(examplesRef, where("songId", "in", validSongIds), where("level", "==", level));
      const snapshot = await getDocs(q);

      const grouped: Record<string, Example[]> = {};
      snapshot.forEach(doc => {
        const docData = doc.data();
        const data = { id: doc.id, ...docData } as Example;
        const songId = docData.songId;
        if (!grouped[songId]) grouped[songId] = [];
        grouped[songId].push(data);
      });

      const orderedResults: Example[] = [];
      for (const songId of songsArray) {
        if (grouped[songId]) {
          orderedResults.push(...grouped[songId]);
        }
      }

      return orderedResults;
    }

    async function getLessonsByLevel(level: number): Promise<Lesson[]> {
      console.log('a', level);
      const lessonsRef = collection(db, "lessons");
      console.log('b');
      const q = query(lessonsRef,
        where("level", "==", level),
        orderBy("order"));
      console.log('c');
      const snapshot = await getDocs(q);
      console.log('d - snapshot size:', snapshot.size);
      const results: Lesson[] = [];
      console.log('e');
      if (snapshot.empty) {
        console.warn('No documents found matching the query for level:', level);
      }
      snapshot.forEach(doc => {
        console.log('f', doc.id);
        results.push({ ...doc.data() } as Lesson);
      });
      console.log('g', results);
      return results;
    }

    (async () => {
      try {
        const ex = await getExamplesBySongs(currentLevel);
        if (mounted) setExamples(ex);

        const ls = await getLessonsByLevel(currentLevel);
        if (mounted) setLessons(ls);

      } catch (err) {
        console.error('Failed to load examples or lessons', err);
      }
    })();

    return () => { mounted = false; };
  }, [currentLevel, db]);

  // derived image sources for the round speed buttons
  const placeholderData = "data:image/svg+xml;utf8," + encodeURIComponent(
    "<svg xmlns='http://www.w3.org/2000/svg' width='400' height='300'><rect fill='%23666' width='100%' height='100%'/><text x='50%' y='50%' fill='%23fff' font-size='20' text-anchor='middle' dominant-baseline='middle'>No Image</text></svg>"
  );

  const videoId = top10Songs?.[currentSong]?.yt?.[top10Songs[currentSong]?.ytIndex ?? 0];
  const location = useLocation();
  const songImgSrc = videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : placeholderData;
  const levelImgSrc = levels[currentLevel - 1]?.image || placeholderData;

  // Determine button visibility based on the current path
  const isSongButtonVisible = ['/lyrics'].includes(location.pathname);
  const isLessonButtonVisible = ['/', '/learn', '/practice', '/play', '/select'].includes(location.pathname);
  const isTranslateButtonVisible = ['/', '/lyrics', '/learn', '/practice', '/play', '/select'].includes(location.pathname);
  // Menu (hamburger) visible on main lesson pages
  const isHamburgerVisible = ['/', '/learn', '/practice', '/play', '/select'].includes(location.pathname);
  const isBackButtonVisible = ['/talk', '/billboard', '/lyrics', '/configure'].includes(location.pathname);

  // Inline SVG components for the translate toggle — prevents color inversion when used as <img>
  const TranslatedSVG: React.FC = () => (
    <svg viewBox="0 0 24 24" width="24" height="24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
      <rect width="24" height="24" rx="4" fill="#000" />
      <text x="50%" y="56%" fill="#ffffff" fontSize="14" fontFamily="SimSun, serif" fontWeight="bold" textAnchor="middle" dominantBaseline="middle">文</text>
    </svg>
  );

  const UntranslatedSVG: React.FC = () => (
    <svg viewBox="0 0 24 24" width="24" height="24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
      <rect width="24" height="24" rx="4" fill="transparent" />
      <text x="50%" y="56%" fill="#ffffff" fontSize="12" fontFamily="Arial, Helvetica, sans-serif" textAnchor="middle" dominantBaseline="middle">A</text>
    </svg>
  );

  return (
    <div className="app-container">
      <div className="background-image"></div>
      <nav className="main-nav">
        {isBackButtonVisible ? (
          <button
            className="back-button"
            onClick={() => navigate('/')}
            title="Back to main page"
            aria-label="Back"
          >
            <BackIcon />
          </button>
        ) : (
          isHamburgerVisible ? (
            <button
              className="speed-button hamburger-button left-hamburger"
              aria-expanded={isNavVisible}
              aria-label="Open menu"
              onClick={toggleNav}
              title="Menu"
            >
              <svg viewBox="0 0 24 24" width="24" height="24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                <rect x="3" y="6" width="18" height="2" rx="1" fill="currentColor" />
                <rect x="3" y="11" width="18" height="2" rx="1" fill="currentColor" />
                <rect x="3" y="16" width="18" height="2" rx="1" fill="currentColor" />
              </svg>
            </button>
          ) : (
            <div className="hamburger-placeholder" style={{ width: '25px', height: '21px' }} />
          )
        )}
        {isNavVisible && (
          <ul className="page-list" ref={navRef}>
            <li className="menu-item">
              <NavLink className={({ isActive }) => `menu-link ${isActive ? 'selected' : ''}`} to="/talk" onClick={() => setIsNavVisible(false)}>
                <div className="menu-thumb"><img src={getMenuIcon('Talk')} alt="Talk" /></div>
                <span className="menu-label">Talk</span>
              </NavLink>
            </li>
            <li className="menu-item">
              <NavLink className={({ isActive }) => `menu-link ${isActive ? 'selected' : ''}`} to="/billboard" onClick={() => setIsNavVisible(false)}>
                <div className="menu-thumb"><img src={getMenuIcon('Billboard')} alt="Billboard" /></div>
                <span className="menu-label">Billboard</span>
              </NavLink>
            </li>
            <li className="menu-item">
              <NavLink className={({ isActive }) => `menu-link ${isActive ? 'selected' : ''}`} to="/lyrics" onClick={() => setIsNavVisible(false)}>
                <div className="menu-thumb"><img src={getMenuIcon('Lyrics')} alt="Lyrics" /></div>
                <span className="menu-label">Lyrics</span>
              </NavLink>
            </li>
            <li className="menu-item">
              <NavLink className={({ isActive }) => `menu-link ${isActive ? 'selected' : ''}`} to="/configure" onClick={() => setIsNavVisible(false)}>
                <div className="menu-thumb"><img src={getMenuIcon('Configure')} alt="Configure" /></div>
                <span className="menu-label">Configure</span>
              </NavLink>
            </li>
          </ul>
        )}
        <div className="speed-buttons">
          {isSongButtonVisible && (
            <button
              className="speed-button"
              aria-label="Song"
              onClick={() => setShowSongList(!showSongList)}
              title={top10Songs[currentSong]?.name || 'Song'}
            >
              <img src={songImgSrc} alt={top10Songs[currentSong]?.name || 'Song'} />
            </button>
          )}

          {isLessonButtonVisible && (
            <button
              className="speed-button"
              aria-label="Lesson"
              onClick={() => setShowLevelList(!showLevelList)}
              title={levels[currentLevel - 1]?.name || 'Level'}
            >
              <img src={levelImgSrc} alt={levels[currentLevel - 1]?.name || 'Level'} />
            </button>
          )}

          {isTranslateButtonVisible && (
            <button
              className="speed-button"
              aria-pressed={translate}
              aria-label="Toggle Translate"
              onClick={() => setTranslate(!translate)}
              title={translate ? 'Translated' : 'Not translated'}
            >
              {translate ? <TranslatedSVG /> : <UntranslatedSVG />}
            </button>
          )}
          
        </div>
      </nav>
      {showSongList && (
        <div className="song-list" ref={songListRef}>
          <ul>
            {top10Songs && top10Songs.length > 0 ? (
              top10Songs.map((song, i) => {
                const vid = song?.yt?.[song.ytIndex ?? 0];
                const thumb = vid ? `https://img.youtube.com/vi/${vid}/hqdefault.jpg` : '';
                const embedUrl = vid ? `https://www.youtube.com/embed/${vid}` : '';
                return (
                  <li key={song.id} className={`song-list-item ${i === currentSong ? 'selected' : ''}`} onClick={() => { setCurrentSong(i); setShowSongList(false); }}>
                    <div className="song-thumb">
                      <img src={thumb || undefined} alt={song.name} data-embed={embedUrl} />
                    </div>
                    <div className="song-meta">
                      <div className="song-top">
                        <span className="song-position">{song.position}</span>
                        <span className="song-artist">{song.artist}</span>
                      </div>
                      <div className="song-bottom">
                        <span className="song-name">{song.name}</span>
                        {translate && song.translation?.[currentLanguage] && (
                          <div className="song-translation">{song.translation[currentLanguage]}</div>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })
            ) : (
              <li className="song-list-empty">No songs</li>
            )}
          </ul>
        </div>
      )}
      {showLevelList && (
        <div className="level-list" ref={levelListRef}>
          <ul>
            {levels && levels.length > 0 ? (
              levels.map((lvl, i) => (
                <li key={`${lvl.level}-${i}`} className={`song-list-item level-list-item ${lvl.level === currentLevel ? 'selected' : ''}`} onClick={() => { setCurrentLevel(lvl.level); setShowLevelList(false); }}>
                  <div className="song-thumb">
                    <img src={lvl.image || undefined} alt={lvl.name} />
                  </div>
                  <div className="song-meta">
                    <div className="song-top">
                      <span className="song-position">{lvl.level}</span>
                    </div>
                    <div className="song-bottom">
                      <span className="song-name">{lvl.name}</span>
                    </div>
                  </div>
                </li>
              ))
            ) : (
              <li className="song-list-empty">No levels</li>
            )}
          </ul>
        </div>
      )}
      <main className="content">
        <Routes>
          <Route path="/" element={<Fly currentLevel={currentLevel} setCurrentLevel={setCurrentLevel} currentSong={currentSong} setCurrentSong={setCurrentSong} currentLanguage={currentLanguage} translate={translate} examples={examples} top10Songs={top10Songs} lessons={lessons} levels={levels} />} />
          <Route path="/talk" element={<Talk />} />
          <Route path="/billboard" element={<Billboard />} />
          <Route path="/lyrics" element={<Lyrics currentSong={currentSong} currentLanguage={currentLanguage} translate={translate} top10Songs={top10Songs} />} />
          <Route path="/learn" element={<Fly currentLevel={currentLevel} setCurrentLevel={setCurrentLevel} currentSong={currentSong} setCurrentSong={setCurrentSong} currentLanguage={currentLanguage} translate={translate} examples={examples} top10Songs={top10Songs} lessons={lessons} levels={levels} />} />
          <Route path="/practice" element={<Fly currentLevel={currentLevel} setCurrentLevel={setCurrentLevel} currentSong={currentSong} setCurrentSong={setCurrentSong} currentLanguage={currentLanguage} translate={translate} examples={examples} top10Songs={top10Songs} lessons={lessons} levels={levels} />} />
          <Route path="/play" element={<Fly currentLevel={currentLevel} setCurrentLevel={setCurrentLevel} currentSong={currentSong} setCurrentSong={setCurrentSong} currentLanguage={currentLanguage} translate={translate} examples={examples} top10Songs={top10Songs} lessons={lessons} levels={levels} />} />
          <Route path="/select" element={<Fly currentLevel={currentLevel} setCurrentLevel={setCurrentLevel} currentSong={currentSong} setCurrentSong={setCurrentSong} currentLanguage={currentLanguage} translate={translate} examples={examples} top10Songs={top10Songs} lessons={lessons} levels={levels} />} />
          <Route path="/configure" element={<Configure currentLanguage={currentLanguage} setCurrentLanguage={setCurrentLanguage} />} />
        </Routes>
      </main>
    </div>
  );
};

export default App;
